import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, ChevronDown } from 'lucide-react';
import { useApp } from '../context/AppContext';

export default function Header() {
  const { state, dispatch } = useApp();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (state.searchQuery.trim()) {
      navigate('/products');
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value });
  };

  const cartItemsCount = state.cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      {/* Top Banner */}
      <div className="bg-red-600 text-white text-center py-2 text-sm">
        🔥 Shop Above 2999/- Get 45% Discount  →
      </div>

      {/* Main Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
              <div className="bg-white p-2 rounded-full">
                <span className="text-black text-xl font-bold">A</span>
              </div>
              <span className="text-2xl font-bold text-white hidden sm:block">AKIDO™</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              <Link to="/products" className="text-white hover:text-gray-300 transition-colors">
                Products
              </Link>
              <Link to="/about" className="text-white hover:text-gray-300 transition-colors">
                About Us
              </Link>
              <Link to="/feedback" className="text-white hover:text-gray-300 transition-colors">
                Feedback
              </Link>
              <Link to="/privacy" className="text-white hover:text-gray-300 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cart" className="text-white hover:text-gray-300 transition-colors">
                Cart
              </Link>
            </nav>

            {/* Search Bar - Always visible on mobile */}
            <form onSubmit={handleSearch} className="flex items-center flex-1 max-w-lg mx-4">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Find your choice |"
                  value={state.searchQuery}
                  onChange={handleSearchInputChange}
                  className="w-full bg-white border border-gray-300 rounded-full py-2 pl-4 pr-12 text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-orange-400 hover:bg-orange-500 p-2 rounded-full transition-colors"
                >
                  <Search className="w-4 h-4 text-black" />
                </button>
              </div>
            </form>

            {/* Right Side Icons */}
            <div className="flex items-center space-x-3">
              <Link to="/cart" className="relative p-2 text-white hover:text-red-400 transition-colors">
                <ShoppingCart className="w-6 h-6" />
                {cartItemsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                    {cartItemsCount}
                  </span>
                )}
              </Link>

              {state.isSignedIn ? (
                <div className="flex items-center space-x-2">
                  <span className="text-white text-sm hidden sm:block">{state.user?.name}</span>
                  <button
                    onClick={() => dispatch({ type: 'SIGN_OUT' })}
                    className="text-white hover:text-red-400 transition-colors"
                  >
                    <User className="w-6 h-6" />
                  </button>
                </div>
              ) : (
                <Link to="/signin" className="text-white hover:text-red-400 transition-colors">
                  <User className="w-6 h-6" />
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden text-white hover:text-red-400"
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-700">
              <nav className="space-y-2">
                <Link
                  to="/products"
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Products
                </Link>
                <Link
                  to="/about"
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link
                  to="/feedback"
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Feedback
                </Link>
                <Link
                  to="/privacy"
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cart"
                  className="block text-white hover:text-red-400 transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Cart
                </Link>
              </nav>
            </div>
          )}
        </div>

        {/* Secondary Navigation */}
        <div className="bg-gray-900 border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center space-x-4 sm:space-x-8 py-3 overflow-x-auto">
              <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
                For Her
              </Link>
              <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
                For Him
              </Link>
              <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
                New
              </Link>
              <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
                Premium
              </Link>
              <Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm whitespace-nowrap">
                High Fragrance
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}